// src/components/reports/bulkGenerate.js
//
// Run the EXISTING per-client generate action over a list of clients, one at a
// time, collecting a result per client.
//
// ⛔ THIS FILE CONTAINS NO GENERATE LOGIC AND MUST NEVER GROW ANY. It is handed
// `generateOne(companyId)` — the same `generateReport(cid, period)` call the
// card's Regenerate button makes — and its only job is ordering, pacing,
// retrying and reporting. A bulk-generated pack is byte-for-byte a Regenerate
// pack because it IS a Regenerate, N times.
//
// ⚠ WHY SEQUENTIAL, and it is not the reason the brief assumed. `/generate` is
// mounted WITHOUT `aiLimiter` (server.js) and the report path calls Anthropic
// nowhere — `execNarrative` is deterministic template code — and it renders no
// PDF either (DocRaptor is the separate download route). The real constraint is
// AUTOTASK: each generate fires several ticket queries per client and
// "Autotask 429s on a burst" is documented in reportService, which already
// sequences its own five queries in two waves for that reason. Firing N clients
// at once would defeat that pacing from the outside.
//
// ⚠ There is also no single all-clients request, by construction: N separate
// requests, so Azure's 230s per-request ceiling applies to one client's generate
// and never to the run.

/** Is this the server telling us to slow down? */
export function isRateLimited(err) {
  const status = err?.response?.status ?? err?.status;
  return status === 429;
}

/** How long to wait before retry `n` (1-based), honouring Retry-After if sent. */
export function backoffMs(err, attempt, base = 2000) {
  const hdr = err?.response?.headers?.["retry-after"];
  const secs = Number(hdr);
  /* The server's own number wins when it sends one — guessing shorter than it
     asked for is how a back-off turns into a second burst. */
  if (Number.isFinite(secs) && secs > 0) return Math.min(secs * 1000, 60000);
  return Math.min(base * 2 ** (attempt - 1), 30000);
}

const wait = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * Generate for each client in turn.
 *
 * @param clients      [{ id, companyName }]
 * @param generateOne  async (companyId) => result   — the EXISTING action
 * @param opts.onProgress   ({ index, total, client, results }) before each client
 * @param opts.onResult     (result) after each client
 * @param opts.shouldCancel () => boolean, checked between clients
 * @param opts.gapMs        pause between clients
 * @param opts.maxRetries   429 retries per client
 * @param opts.sleep        injectable, so tests do not actually wait
 *
 * @returns { results, cancelled, ok, failed }
 */
export async function runBulkGenerate(clients, generateOne, opts = {}) {
  const {
    onProgress, onResult, shouldCancel,
    gapMs = 600, maxRetries = 3, sleep = wait,
  } = opts;

  const list = Array.isArray(clients) ? clients : [];
  const results = [];
  let cancelled = false;

  for (let i = 0; i < list.length; i++) {
    /* ⚠ Checked BEFORE the call, never mid-flight. A generate that has already
       reached the server writes its blob and re-freezes its snapshots whatever
       the browser does next, so "cancelled" must mean "not started" — reporting
       a client as cancelled while its pack was in fact rewritten would be a lie
       about what is now stored. */
    if (shouldCancel && shouldCancel()) { cancelled = true; break; }

    const client = list[i];
    const cid = String(client.id);
    onProgress?.({ index: i, total: list.length, client, results });

    let attempt = 0;
    for (;;) {
      try {
        const value = await generateOne(cid);
        /* `retried` is carried on the SUCCESS path too, not just on failure —
           a client that needed two attempts before it went through is worth
           seeing in the summary, and its absence read as zero. */
        const r = { companyId: cid, companyName: client.companyName, ok: true, value, retried: attempt };
        results.push(r); onResult?.(r);
        break;
      } catch (err) {
        /* ⛔ ONLY 429 IS RETRIED. A 500 from a broken source PDF, or a 409 from
           a guard, will fail again identically — retrying it three times just
           delays the summary and triples the load. Rate limiting is the one
           failure that a pause genuinely fixes. */
        if (isRateLimited(err) && attempt < maxRetries) {
          attempt++;
          await sleep(backoffMs(err, attempt));
          continue;
        }
        const r = {
          companyId: cid,
          companyName: client.companyName,
          ok: false,
          /* Surfaced verbatim where the server bothered to explain itself — the
             identity and duplicate guards return wording written to be read. */
          error: err?.response?.data?.error || err?.message || "Generate failed",
          status: err?.response?.status ?? null,
          retried: attempt,
        };
        results.push(r); onResult?.(r);
        break;
      }
    }

    /* ⚠ The pause goes BETWEEN clients only — never after the last one, which
       would just make the run feel slower than it is. */
    if (i < list.length - 1 && gapMs > 0) await sleep(gapMs);
  }

  return {
    results,
    cancelled,
    ok: results.filter((r) => r.ok).length,
    failed: results.filter((r) => !r.ok).length,
  };
}
